/**
 * PolicyEvaluator — Purpose-based consent policy engine.
 *
 * Evaluates whether a script/vendor should be allowed to run based on:
 * 1. The user's consent decisions
 * 2. The applicable lawful basis (consent vs legitimate interest)
 * 3. The jurisdiction/regulation in effect
 *
 * This moves enforcement beyond simple category toggles into proper
 * per-vendor, per-purpose evaluation — matching enterprise CMP behaviour.
 */

import type { ConsentDecisions } from "./StorageAdapter";

export type ConsentCategory = "strictly_necessary" | "analytics" | "advertising" | "functional" | "social";
export type LawfulBasis = "consent" | "legitimate_interest" | "contract" | "legal_obligation" | "vital_interest" | "public_task";
export type PolicyDecision = "allow" | "block" | "pending";

export interface PolicyResult {
  decision: PolicyDecision;
  reason: string;
}

export interface PolicyParams {
  decisions: ConsentDecisions;
  category: ConsentCategory;
  lawfulBasis?: LawfulBasis;
  /** True if user explicitly clicked "Reject All" or toggled off this category */
  userRejected?: boolean;
  /** GDPR or CCPA regulation string */
  regulation?: string;
}

// Vendors where legitimate interest is typically claimed (IAB TCF standard purposes)
const LI_VENDORS = new Set([
  "linkedin", "outbrain", "taboola", "criteo", "appnexus",
]);

export class PolicyEvaluator {
  /**
   * Evaluate whether a specific vendor/script should be allowed.
   */
  evaluate(params: PolicyParams): PolicyResult {
    const { decisions, category, lawfulBasis, userRejected, regulation } = params;

    // Strictly necessary — always allow, no consent needed
    if (category === "strictly_necessary") {
      return { decision: "allow", reason: "Strictly necessary — no consent required" };
    }

    // Under CCPA (opt-out model) — allow unless user explicitly opted out
    if (regulation === "ccpa") {
      if (userRejected || decisions[category] === false) {
        return { decision: "block", reason: "User opted out (CCPA)" };
      }
      return { decision: "allow", reason: "CCPA opt-out model — not opted out" };
    }

    // Legitimate interest basis (GDPR Art. 6(1)(f))
    // Allow unless user has explicitly objected
    if (lawfulBasis === "legitimate_interest") {
      if (userRejected) {
        return { decision: "block", reason: "User objected to legitimate interest processing" };
      }
      // For advertising/social under LI, require explicit consent per stricter interpretations
      if (category === "advertising" || category === "social") {
        const consented = decisions[category];
        if (!consented) {
          return { decision: "block", reason: "Advertising/social requires explicit consent despite LI claim" };
        }
        return { decision: "allow", reason: "Consented to advertising/social" };
      }
      return { decision: "allow", reason: "Legitimate interest — no objection" };
    }

    // Contract / Legal obligation / Vital interest / Public task — always allow
    if (
      lawfulBasis === "contract" ||
      lawfulBasis === "legal_obligation" ||
      lawfulBasis === "vital_interest" ||
      lawfulBasis === "public_task"
    ) {
      return { decision: "allow", reason: `Lawful basis: ${lawfulBasis}` };
    }

    // Default: consent-based
    const consented = decisions[category];
    if (consented === undefined) {
      return { decision: "pending", reason: "Consent not yet recorded" };
    }
    if (consented) {
      return { decision: "allow", reason: "User consented to category" };
    }
    return { decision: "block", reason: "User did not consent to category" };
  }

  /**
   * Batch-evaluate a set of categories and return the full picture.
   */
  evaluateAll(
    decisions: ConsentDecisions,
    regulation: string = "gdpr"
  ): Record<ConsentCategory, PolicyResult> {
    const cats: ConsentCategory[] = ["strictly_necessary", "analytics", "advertising", "functional", "social"];
    const result = {} as Record<ConsentCategory, PolicyResult>;
    for (const cat of cats) {
      result[cat] = this.evaluate({ decisions, category: cat, regulation });
    }
    return result;
  }
}
